/**
 * Where an approval decision is actually made.
 *
 * The queue row is a summary; this is the whole submission — every photo, the description,
 * each size with its price, and the fee that will apply, editable before the decision
 * rather than after. That last part is the point: once a product is live it is on sale at
 * whatever fee was in force, so "set the fee, then approve" is one action in a reviewer's
 * head and should be one on screen too.
 *
 * It is a route rather than a panel wedged into the list, like every other detail in the
 * console: a decision this consequential should be linkable, so somebody can be sent the
 * exact product being argued about.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Boxes,
  Check,
  ClipboardCheck,
  Clock,
  IndianRupee,
  ImageOff,
  Images,
  Info,
  Layers,
  PackageSearch,
  Percent,
  PillBottle,
  Sparkles,
  Store,
  X,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { DetailPanel, Field, FieldList, PanelEmpty, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { notify } from '@/components/ui/toaster';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import { CommissionEditor } from '@/features/commission/components/commission-editor';
import { date_time, humanize, inr } from '@/lib/format';
import { use_product_categories } from '@/features/product-categories/hooks';
import { category_label, type ProductCategory } from '@/features/product-categories/types';
import {
  use_approve_product,
  use_override_commission,
  use_product_detail,
  use_reject_product,
} from '@/features/product-approval/hooks';
import {
  FIELD_LABEL,
  KIND_LABEL,
  KIND_TONE,
  SOURCE_LABEL,
  SOURCE_TONE,
  VERTICAL_LABEL,
  asks_for,
  days_waiting,
  fee_amount,
  field_value,
  publish_blocked,
  queue_kind,
  wait_tone,
  type QueueDetail,
  type VariantPricing,
  type Vertical,
} from '@/features/product-approval/types';

export function ProductReviewPage() {
  const { vertical, id } = useParams<{ vertical: string; id: string }>();
  const navigate = useNavigate();
  const v = (vertical?.toUpperCase() as Vertical) ?? 'FOOD';

  const { data: item, isLoading, error } = use_product_detail(v, id);
  const { data: all_categories } = use_product_categories();
  const approve_m = use_approve_product();
  const reject_m = use_reject_product();
  const product_fee_m = use_override_commission();

  const [rejecting, set_rejecting] = useState(false);
  const [reject_error, set_reject_error] = useState<string | null>(null);

  const busy = approve_m.isPending || reject_m.isPending;
  const kind = item ? queue_kind(item) : 'new';
  const decidable = !!item && (item.approval_status === 'PENDING' || !!item.pending_changes);
  /*
   * The backend refuses to publish a product a shopper would see no picture for, so the
   * console must not offer a button that comes back 422. Send back stays available — it is
   * the right action here, and the reason goes to the person who can fix it.
   */
  const blocked = !!item && publish_blocked(item);

  async function approve() {
    if (!item) return;
    try {
      await approve_m.mutateAsync({ vertical: item.vertical, id: item.id });
      notify.success(item.pending_changes ? 'Change approved' : 'Product published', {
        description: item.name,
      });
    } catch (err) {
      notify.error(err, 'Could not approve this product.');
    }
  }

  async function reject(reason: string) {
    if (!item) return;
    set_reject_error(null);
    try {
      await reject_m.mutateAsync({ vertical: item.vertical, id: item.id, reason });
      notify.success('Sent back to the vendor', { description: item.name });
      set_rejecting(false);
      navigate('/product-approvals');
    } catch (err) {
      set_reject_error(err instanceof Error ? err.message : 'Could not send this product back.');
    }
  }

  if (error) {
    return (
      <DashboardLayout title="Product review" breadcrumbs={[{ label: 'Review' }]}>
        <ErrorState
          title="Could not load this product"
          description="It may have been removed since the queue was loaded."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={item?.name ?? 'Product review'}
      subtitle={item ? asks_for(item) : undefined}
      breadcrumbs={[{ label: item?.name ?? 'Review' }]}
      title_badges={
        item ? (
          <>
            <Badge tone={KIND_TONE[kind]} variant="solid" dot data-testid="review-kind">
              {KIND_LABEL[kind]}
            </Badge>
            <Badge tone="neutral">{VERTICAL_LABEL[item.vertical]}</Badge>
            {item.is_prescription_required && (
              <Badge tone="destructive" data-testid="review-rx">
                <PillBottle className="h-3 w-3" /> Prescription
              </Badge>
            )}
          </>
        ) : undefined
      }
      meta={
        item?.vendor ? (
          <Link
            to={`/vendors/${item.vendor.id}`}
            className="inline-flex items-center gap-1.5 underline-offset-2 hover:text-primary hover:underline"
          >
            <Store className="h-3.5 w-3.5" />
            {item.vendor.business_name ?? 'No business name'}
            {item.vendor.city ? ` · ${item.vendor.city}` : ''}
          </Link>
        ) : undefined
      }
      actions={
        decidable ? (
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy || blocked}
              onClick={() => void approve()}
              data-testid="review-approve"
              title={blocked ? 'This product has no photo, so it cannot go on sale' : undefined}
            >
              <Check className="mr-1 h-4 w-4" />
              {item?.pending_changes ? 'Approve the change' : 'Approve and publish'}
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => set_rejecting(true)}
              data-testid="review-reject"
            >
              <X className="mr-1 h-4 w-4" /> Send back
            </Button>
          </div>
        ) : undefined
      }
      stats={<MetricStrip metrics={build_metrics(item, all_categories)} />}
    >
      <div className="grid items-start gap-gutter lg:grid-cols-[1.35fr_1fr]">
        <div className="grid gap-gutter">
          <DecisionPanel item={item} loading={isLoading} blocked={blocked} />
          <PhotosPanel item={item} loading={isLoading} />
          <PricingPanel
            item={item}
            loading={isLoading}
            saving={product_fee_m.isPending}
            on_save_product_fee={(next) =>
              product_fee_m.mutateAsync({ vertical: v, product_id: id as string, ...next })
            }
          />
        </div>

        <div className="grid gap-gutter">
          <DetailPanel title="The listing" icon={Info} test_id="panel-listing">
            {isLoading || !item ? (
              <PanelSkeleton />
            ) : (
              <FieldList>
                <Field label="Brand" value={item.brand} />
                <Field
                  label="Category"
                  value={category_label(all_categories, item.vertical, item.category)}
                />
                <Field label="Pack size" value={item.pack_size} />
                <Field
                  label="On sale"
                  value={
                    <Badge tone={item.is_available ? 'success' : 'neutral'} size="sm" dot>
                      {item.is_available ? 'Vendor has it switched on' : 'Switched off by the vendor'}
                    </Badge>
                  }
                />
                <Field
                  label="Source"
                  value={
                    item.catalog_product_id ? (
                      /*
                       * Worth saying plainly: on a linked listing the name, brand and
                       * category are the catalogue's, not this vendor's — so sending it
                       * back over its name asks the vendor to fix something they cannot.
                       */
                      <span className="flex flex-col gap-1">
                        <Link
                          to={`/catalogue?q=${encodeURIComponent(item.name)}`}
                          className="inline-flex w-fit items-center gap-1.5 underline-offset-2 hover:text-primary hover:underline"
                          data-testid="review-catalogue-link"
                        >
                          <Sparkles className="h-3.5 w-3.5" /> From the catalogue
                        </Link>
                        <span className="text-micro text-muted-foreground">
                          Name, brand and category are the catalogue’s — the vendor cannot
                          change them.
                        </span>
                      </span>
                    ) : (
                      'The vendor’s own product'
                    )
                  }
                />
                <Field label="Product ID" value={item.id} mono />
                {item.reviewed_at && (
                  <Field label="Last reviewed" value={date_time(item.reviewed_at)} />
                )}
                {item.approval_note && (
                  <Field
                    label="Last note"
                    value={<span className="text-destructive">{item.approval_note}</span>}
                    test_id="review-note"
                  />
                )}
              </FieldList>
            )}
          </DetailPanel>

          <StockPanel item={item} loading={isLoading} />

          {item?.description && (
            <DetailPanel title="Description" icon={ClipboardCheck} test_id="panel-description">
              <p className="whitespace-pre-line text-data" data-testid="review-description">
                {item.description}
              </p>
            </DetailPanel>
          )}
        </div>
      </div>

      <ReasonModal
        open={rejecting}
        title="Send this product back"
        description={`The vendor reads this. Say what needs to change about “${item?.name ?? ''}”.`}
        confirm_label="Send back"
        variant="destructive"
        loading={reject_m.isPending}
        error={reject_error}
        on_confirm={reject}
        on_cancel={() => {
          set_rejecting(false);
          set_reject_error(null);
        }}
      />
    </DashboardLayout>
  );
}

function build_metrics(
  item: QueueDetail | undefined,
  categories: ProductCategory[] | undefined,
): Metric[] {
  const days = item ? days_waiting(item) : 0;
  const prices = (item?.pricing ?? []).map((p) => Number(p.customer_price_inr));
  const cheapest = prices.length ? Math.min(...prices) : null;
  const dearest = prices.length ? Math.max(...prices) : null;
  const source = item?.pricing?.[0]?.commission_source;
  const empty = (item?.stock ?? []).filter((s) => !s.is_in_stock).length;

  return [
    {
      key: 'waiting',
      label: 'Waiting',
      value: item ? (days === 0 ? 'Today' : `${days}d`) : undefined,
      hint: item?.pending_changes ? 'Since the edit was filed' : 'Since it was submitted',
      tone: item ? wait_tone(days) : 'default',
      icon: Clock,
      loading: !item,
    },
    {
      key: 'photos',
      label: 'Photos',
      value: item ? String(item.visible_photos) : undefined,
      hint:
        item && item.visible_photos === 0
          ? 'Required before it can go on sale'
          : 'Vendor’s own, or the catalogue’s',
      tone: item && publish_blocked(item) ? 'destructive' : 'default',
      icon: Images,
      loading: !item,
    },
    {
      key: 'sizes',
      label: 'Sizes priced',
      value: item ? String(item.pricing.length) : undefined,
      hint:
        !item
          ? undefined
          : item.pricing.length === 0
            ? 'Nothing is purchasable yet'
            : empty > 0
              ? `${empty} with no stock`
              : 'Every size has stock',
      tone: item && (item.pricing.length === 0 || empty === item.stock.length) ? 'warning' : 'default',
      icon: Layers,
      loading: !item,
    },
    {
      key: 'price',
      label: 'Customer pays',
      value:
        cheapest === null
          ? item
            ? '—'
            : undefined
          : cheapest === dearest
            ? inr(cheapest)
            : `${inr(cheapest)}–${inr(dearest as number)}`,
      hint: 'Vendor price plus the fee',
      icon: IndianRupee,
      loading: !item,
    },
    {
      key: 'fee',
      label: 'Fee comes from',
      // The rung of the chain that won. "10%" without this tells an admin nothing about
      // what changing it would touch.
      value: source ? SOURCE_LABEL[source].replace(' — the vendor keeps everything', '') : item ? '—' : undefined,
      hint: item ? category_label(categories, item.vertical, item.category) : undefined,
      tone: source === 'NONE' ? 'destructive' : 'default',
      icon: Percent,
      loading: !item,
    },
  ];
}

/**
 * What is actually being asked.
 *
 * For an edit that is the diff and nothing else — reading a whole product and hoping to
 * spot one altered dosage is how a bad change gets waved through.
 */
function DecisionPanel({
  item,
  loading,
  blocked,
}: {
  item: QueueDetail | undefined;
  loading: boolean;
  blocked: boolean;
}) {
  if (loading || !item) {
    return (
      <DetailPanel title="What you’re deciding" icon={ClipboardCheck}>
        <PanelSkeleton rows={3} />
      </DetailPanel>
    );
  }

  const p = item.pending_changes;

  return (
    <DetailPanel
      title="What you’re deciding"
      icon={ClipboardCheck}
      description={asks_for(item)}
      test_id="panel-decision"
    >
      {blocked && (
        <p
          className="mb-2 flex items-start gap-2 rounded-control bg-destructive/10 px-2.5 py-2 text-data font-medium text-destructive"
          data-testid="review-photo-required"
        >
          <ImageOff className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Nothing here shows a shopper a picture, so this cannot go on sale. Send it back
            asking for a photo, or add one to its catalogue entry.
          </span>
        </p>
      )}
      {p ? (
        <div className="grid gap-2">
          <p className="rounded-control bg-warning/10 px-2.5 py-1.5 text-data font-medium text-warning">
            Customers still see the approved version until you decide.
          </p>
          <dl className="divide-y divide-border/60" data-testid="review-diff">
            {p.fields.map((f) => (
              <div
                key={f.field}
                className="grid gap-1 py-2 first:pt-0 sm:grid-cols-[minmax(6rem,9rem)_1fr]"
                data-testid={`diff-${f.field}`}
              >
                <dt className="text-data font-medium text-muted-foreground">
                  {FIELD_LABEL[f.field] ?? humanize(f.field)}
                </dt>
                <dd className="flex flex-wrap items-baseline gap-2 text-data">
                  <span className="text-muted-foreground line-through">
                    {field_value(f.current, f.field)}
                  </span>
                  <span aria-hidden="true" className="text-muted-foreground">
                    →
                  </span>
                  <span className="font-semibold">{field_value(f.proposed, f.field)}</span>
                </dd>
              </div>
            ))}
            {p.images_changed && (
              <div className="py-2" data-testid="diff-images">
                <p className="text-data font-medium text-warning">
                  New photos submitted — held back until you approve.
                </p>
              </div>
            )}
            {p.fields.length === 0 && !p.images_changed && (
              <p className="py-2 text-data text-muted-foreground">
                An edit was filed with nothing in it. Approving clears it.
              </p>
            )}
          </dl>
        </div>
      ) : item.approval_status === 'PENDING' ? (
        <p className="text-data text-muted-foreground">
          A first review. Nothing here has been published, so read the whole listing below —
          the photos, the description and what each size will cost a shopper.
        </p>
      ) : item.approval_status === 'REJECTED' ? (
        <p className="text-data text-muted-foreground">
          This was sent back and never published.{' '}
          {item.approval_note ? `Reason given: “${item.approval_note}”.` : ''}
        </p>
      ) : (
        <p className="text-data text-muted-foreground">
          This product is live and has no edits waiting. Nothing to decide.
        </p>
      )}
    </DetailPanel>
  );
}

function PhotosPanel({ item, loading }: { item: QueueDetail | undefined; loading: boolean }) {
  if (loading || !item) {
    return (
      <DetailPanel title="Photos" icon={PackageSearch}>
        <PanelSkeleton rows={2} />
      </DetailPanel>
    );
  }

  const curated = item.catalog_images ?? [];

  return (
    <DetailPanel
      title="Photos"
      icon={PackageSearch}
      description="What a shopper will actually see — at least one is required to publish"
      test_id="panel-photos"
    >
      <div className="grid gap-3">
        {/*
          Both sets, because the reviewer needs what CUSTOMERS will see. A linked listing
          usually has no vendor photos at all — the curated ones are what appear in the
          shop, and reporting "no photos" about such a product is both wrong and the sort of
          thing that gets a good listing sent back.
        */}
        {curated.length > 0 && (
          <div>
            <p className="mb-1.5 text-micro font-bold uppercase tracking-wide text-muted-foreground">
              From the catalogue ({curated.length}) — what shoppers see
            </p>
            <ul className="flex flex-wrap gap-2" data-testid="review-catalog-images">
              {curated.map((img, i) => (
                <li key={img.id}>
                  <AuthImage
                    path={`${img.path}?variant=full`}
                    alt={`${item.name} catalogue photo ${i + 1}`}
                    className="h-28 w-28 overflow-hidden rounded-card border border-primary/25 object-cover"
                    data-testid={`review-catalog-image-${img.id}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          {(curated.length > 0 || item.images.length > 0) && (
            <p className="mb-1.5 text-micro font-bold uppercase tracking-wide text-muted-foreground">
              {curated.length > 0 ? 'The vendor’s own photos' : 'The vendor’s photos'}
              {item.images.length > 0 ? ` (${item.images.length})` : ''}
            </p>
          )}
          {item.images.length === 0 ? (
            <p
              className="flex items-center gap-1.5 text-data text-muted-foreground"
              data-testid="review-no-images"
            >
              <ImageOff className="h-4 w-4" />
              {curated.length > 0
                ? 'None of their own — the catalogue photos above are what customers see.'
                : 'The vendor submitted no photos.'}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="review-images">
              {item.images.map((img, i) => (
                <li key={img.id} className="relative">
                  <AuthImage
                    path={`${img.path}?variant=full`}
                    alt={`${item.name} photo ${i + 1}`}
                    className="h-28 w-28 overflow-hidden rounded-card border border-border object-cover"
                    data-testid={`review-image-${img.id}`}
                  />
                  {!img.is_approved && (
                    <Badge
                      tone="warning"
                      variant="solid"
                      size="sm"
                      className="absolute bottom-1 left-1"
                      data-testid={`review-image-held-${img.id}`}
                    >
                      New — held
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DetailPanel>
  );
}

function StockPanel({ item, loading }: { item: QueueDetail | undefined; loading: boolean }) {
  return (
    <DetailPanel
      title="Stock"
      icon={Boxes}
      description="Per size, as the vendor keeps it"
      test_id="panel-stock"
    >
      {loading || !item ? (
        <PanelSkeleton rows={2} />
      ) : item.stock.length === 0 ? (
        <PanelEmpty icon={Boxes}>No sizes on this product yet.</PanelEmpty>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {item.stock.map((s) => (
            /*
              "Not tracked" is its own state rather than 0: a vendor who has not opted into
              counting is always-available, not sold out, and telling a reviewer a new
              listing is empty is worse than saying nothing.
            */
            <li key={s.variant_id}>
              <Badge
                tone={!s.is_in_stock ? 'destructive' : s.is_tracked ? 'neutral' : 'info'}
                size="sm"
                data-testid={`review-stock-${s.variant_id}`}
                data-in-stock={s.is_in_stock}
                title={s.is_tracked ? `${s.stock_qty} on hand` : 'The vendor does not count this size'}
              >
                {s.label}:{' '}
                {!s.is_tracked
                  ? 'not counted'
                  : s.stock_qty === 0
                    ? 'out of stock'
                    : `${s.stock_qty} left`}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </DetailPanel>
  );
}

/** Prices, the fee chain, and the ability to change it before saying yes. */
function PricingPanel({
  item,
  loading,
  saving,
  on_save_product_fee,
}: {
  item: QueueDetail | undefined;
  loading: boolean;
  saving: boolean;
  on_save_product_fee: (next: {
    mode: 'RATE' | 'FLAT' | null;
    rate_pct?: number;
    amount_inr?: number;
  }) => Promise<unknown>;
}) {
  const first = item?.pricing?.[0];

  return (
    <DetailPanel
      title="Prices, and what a customer will pay"
      icon={IndianRupee}
      description="The fee is settled here, before the product goes on sale"
      test_id="panel-pricing"
    >
      {loading || !item ? (
        <PanelSkeleton rows={3} />
      ) : item.pricing.length === 0 ? (
        <span data-testid="review-no-prices">
          <PanelEmpty icon={PackageSearch}>
            No sizes priced yet — nothing on this product is purchasable until the vendor
            prices one.
          </PanelEmpty>
        </span>
      ) : (
        <div className="grid gap-3">
          <div className="divide-y divide-border/60" data-testid="review-prices">
            {item.pricing.map((row) => (
              <PriceRow key={row.variant_id} row={row} vertical={item.vertical} />
            ))}
          </div>

          {/*
            One fee for the whole product, above the per-size overrides: most decisions are
            "this product costs us X", and repeating that per size invites drift.
          */}
          <div className="border-t border-border pt-3">
            <p className="mb-1.5 text-micro font-bold uppercase tracking-wide text-muted-foreground">
              Fee for every size of this product
            </p>
            <CommissionEditor
              testid={`product-${item.id}`}
              value={{
                mode: first?.commission_source === 'PRODUCT' ? (first.commission_mode ?? null) : null,
                rate_pct: first?.commission_rate_pct ?? null,
                amount_inr: null,
                is_override: first?.commission_source === 'PRODUCT',
              }}
              inherited_label={first ? SOURCE_LABEL[first.commission_source] : 'inherited'}
              effective={
                first
                  ? {
                      mode: first.commission_mode,
                      rate_pct: first.commission_rate_pct,
                      amount_inr:
                        first.commission_mode === 'FLAT' ? Number(first.platform_fee_inr) : null,
                    }
                  : undefined
              }
              saving={saving}
              on_save={on_save_product_fee}
            />
          </div>
        </div>
      )}
    </DetailPanel>
  );
}

function PriceRow({ row, vertical }: { row: VariantPricing; vertical: Vertical }) {
  const override_m = use_override_commission();
  const [open, set_open] = useState(false);

  return (
    <div className="py-2 first:pt-0" data-testid={`review-price-${row.variant_id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="min-w-16 text-data font-semibold">{row.label}</span>
        <span className="text-micro text-muted-foreground tabular">
          vendor {inr(row.vendor_price_inr)}
          {row.mrp_inr ? ` · MRP ${inr(row.mrp_inr)}` : ''}
        </span>
        <span className="text-micro text-muted-foreground tabular">
          fee {inr(row.platform_fee_inr)}
          {row.commission_mode === 'RATE' && row.commission_rate_pct != null
            ? ` (${row.commission_rate_pct}%)`
            : ''}
        </span>
        <span className="ml-auto flex items-baseline gap-1.5">
          <span className="text-micro text-muted-foreground">customer pays</span>
          <span
            className="font-heading text-base font-bold tabular text-primary"
            data-testid={`review-customer-price-${row.variant_id}`}
          >
            {inr(row.customer_price_inr)}
          </span>
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Badge tone={SOURCE_TONE[row.commission_source]} size="sm">
          {SOURCE_LABEL[row.commission_source]}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5 text-micro"
          onClick={() => set_open((x) => !x)}
          data-testid={`review-fee-toggle-${row.variant_id}`}
        >
          {open ? 'Close' : 'Change the fee for this size'}
        </Button>
        {row.exceeds_mrp && (
          /* Worth surfacing to the reviewer, not just the vendor: a price above its own
             printed MRP reads as an error to a shopper. */
          <span
            className="text-micro font-medium text-destructive"
            data-testid={`review-exceeds-mrp-${row.variant_id}`}
          >
            With the fee, customers pay more than the MRP
          </span>
        )}
      </div>

      {open && (
        <div className="mt-1.5">
          <CommissionEditor
            testid={row.variant_id}
            value={{
              mode: row.commission_source === 'VARIANT' ? row.commission_mode : null,
              rate_pct: row.commission_rate_pct,
              amount_inr: row.commission_mode === 'FLAT' ? Number(row.platform_fee_inr) : null,
              is_override: row.commission_source === 'VARIANT',
            }}
            inherited_label={SOURCE_LABEL[row.commission_source]}
            /*
             * The pricing row already carries the RESOLVED fee, whichever level set it — so
             * that is exactly what "applies today" means here, no second lookup needed.
             */
            effective={{
              mode: row.commission_mode,
              rate_pct: row.commission_rate_pct,
              amount_inr: row.commission_mode === 'FLAT' ? Number(row.platform_fee_inr) : null,
            }}
            saving={override_m.isPending}
            on_save={(next) =>
              override_m.mutateAsync({ vertical, variant_id: row.variant_id, ...next })
            }
          />
          <p className="mt-1 text-micro text-muted-foreground">
            Applies to this size only. For every size, use the product fee below.
          </p>
        </div>
      )}
    </div>
  );
}

/** Re-exported so the fee wording stays identical wherever it is shown. */
export { fee_amount };
